import React, { useRef } from 'react';
import { motion, useScroll, useTransform } from 'motion/react';
import { Link } from 'react-router-dom';
import {
  UserSquare2,
  ArrowRight,
  CheckCircle,
  TrendingUp,
  Users,
  Brain,
  Mic,
  Database,
  Zap,
  Cpu,
  Play,
  ChevronRight,
  Volume2,
  Languages,
  Cloud,
  Heart,
  GraduationCap,
  Home,
  Scale,
  Store,
  Network,
} from 'lucide-react';
import { ImageWithFallback } from '../../figma/ImageWithFallback';
import { ProductTestimonials } from '../../shared/ProductTestimonials';
import { whispeRAGonTestimonials } from '../../shared/testimonialData';

const WhispeRAGon: React.FC = () => {
  const containerRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: containerRef });
  const backgroundY = useTransform(scrollYProgress, [0, 1], ['0%', '50%']);

  const features = [
    {
      icon: Languages,
      title: '	Cloud-Native Multilingual Avatar',
      description: 'Runs in the cloud and supports multiple languages out of the box, making it suitable for global teams and customer bases.'
    },
    {
      icon: Volume2,
      title: '	Domain-Specific Knowledge via RAG',
      description: 'Connects to your documents, portals and knowledge bases using retrieval-augmented generation, so answers stay accurate and on-brand.'
    },
    {
      icon: Database,
      title: '	Speech, Text & Lip-Sync',
      description: 'Combines Whisper speech-to-text, natural text generation and viseme-based lip-sync to deliver smooth spoken and typed conversations.'
    },
    {
      icon: Brain,
      title: '	Vertical Switching with Simple Data Uploads',
      description: 'Can be reconfigured for healthcare, legal, real estate, education or enterprise service desks by uploading relevant datasets and FAQs.'
    },
    {
      icon: Cloud,
      title: '	Enterprise-Scale Architecture',
      description: 'Supports multi-tenant deployments and high concurrent traffic, so a single platform can serve multiple sites, brands or regions.'
    },
    {
      icon: Zap,
      title: '	Memory Layer for Personalisation',
      description: ' Remembers previous interactions (within agreed boundaries) to personalise follow-ups, recommendations and next-best actions.'
    },
    {
      icon: Cpu,
      title: '	Configurable Personality & Tone',
      description: ': Lets teams tune the avatar’s tone, level of formality and escalation rules to match their brand and risk appetite.'
    },
    {
      icon: Users,
      title: '	Analytics & Insight Dashboard',
      description: 'Tracks volumes, topics, satisfaction signals and deflection rates, giving leaders clear visibility into business impact.'
    }
  ];

  const interactionFlow = [
    {
      icon: Mic,
      title: 'User Input',
      subtitle: 'Voice or text query',
      description: 'Users interact through spoken queries using Whisper speech recognition or typed text input.',
      color: 'from-yellow-400 to-orange-500',
      step: 1
    },
    {
      icon: Cpu,
      title: 'Processing',
      subtitle: 'Whisper/Ollama pipeline',
      description: 'Audio is transcribed via Whisper, or text is directly processed by Ollama for intent understanding.',
      color: 'from-orange-400 to-amber-500',
      step: 2
    },
    {
      icon: Database,
      title: 'Vector Retrieval',
      subtitle: 'RAG memory search',
      description: 'Vector database retrieves relevant domain-specific knowledge to augment the LLM response.',
      color: 'from-amber-400 to-yellow-500',
      step: 3
    },
    {
      icon: Brain,
      title: 'LLM Reasoning',
      subtitle: 'Context-aware generation',
      description: 'Open-source LLM generates accurate, contextual answers using retrieved knowledge and conversation history.',
      color: 'from-yellow-400 to-lime-500',
      step: 4
    },
    {
      icon: Volume2,
      title: 'TTS & Viseme',
      subtitle: 'Natural speech output',
      description: 'Text-to-speech converts response to audio, while viseme data drives realistic lip-sync animations.',
      color: 'from-lime-400 to-green-500',
      step: 5
    },
    {
      icon: UserSquare2,
      title: 'Avatar Reply',
      subtitle: 'Synchronized digital human',
      description: 'Digital human avatar delivers response with synchronized speech and facial expressions for natural interaction.',
      color: 'from-green-400 to-emerald-500',
      step: 6
    }
  ];

  const solutions = [
    {
      icon: Mic,
      title: '	Healthcare Portals',
      description: ' Guide patients through appointment booking, lab result explanations and pre-visit instructions, freeing human staff to focus on clinical priorities.',
      features: ['Multi-Language ASR', 'Noise Cancellation', 'Accent Adaptation', 'Real-Time Processing', 'High Accuracy']
    },
    {
      icon: Database,
      title: '	Legal & Compliance Services',
      description: 'Offer first-line guidance on procedures, required documents and regulatory steps, while clearly flagging when a human expert should take over.',
      features: ['Vector Embeddings', 'Semantic Search', 'Fast Retrieval', 'Domain-Specific Data', 'Dynamic Updates']
    },
    {
      icon: Brain,
      title: '	Real Estate & Property Management',
      description: 'Answer queries about listings, eligibility, maintenance requests and community rules, keeping prospective buyers and tenants engaged 24/7.',
      features: ['Context Awareness', 'Conversation Memory', 'Reasoning Engine', 'Multi-Turn Dialog', 'Answer Quality']
    },
    {
      icon: UserSquare2,
      title: '	Education & Training Platforms',
      description: ' Act as a digital tutor or advisor, helping learners navigate courses, deadlines and institutional processes without waiting in support queues.',
      features: ['Viseme Lip-Sync', 'Facial Animation', 'Gesture Control', 'Customizable Appearance', 'Emotional Expression']
    },
    {
      icon: Cloud,
      title: 'Enterprise & Industrial Service Desks: ',
      description: 'Support employees and partners with HR, IT or operational questions, reducing tickets and call-center load while keeping internal services responsive.',
      features: ['Cloud Scalability', 'Load Balancing', 'High Availability', 'Multi-Tenant Support', 'API Integration']
    }
  ];

  const compliances = [
    {
      icon: Heart,
      title: '	Secure Cloud Deployment',
      description: 'Runs in hardened cloud environments with network-level protections and encryption for data in transit and at rest.',
      benefits: ['24/7 Patient Support', 'Symptom Assessment', 'Appointment Booking', 'Medical FAQs', 'Prescription Reminders']
    },
    {
      icon: Scale,
      title: '	Data Isolation & Multi-Tenancy Controls',
      description: 'Keeps each tenant’s data logically separated, so content from one client or business unit is never surfaced to another.',
      benefits: ['Legal Research', 'Document Review', 'Case Information', 'Compliance Guidance', 'Client Intake']
    },
    {
      icon: Home,
      title: '	Granular Access Management',
      description: 'Role-based access control ensures only authorised users can view logs, configure knowledge sources or manage tenants.',
      benefits: ['Property Search', 'Virtual Tours', 'Mortgage Info', 'Market Insights', 'Scheduling Viewings']
    },
    {
      icon: GraduationCap,
      title: '	Audit Trails & Interaction Logging',
      description: 'Captures detailed logs of conversations and configuration changes to support audits, quality checks and incident review.',
      benefits: ['Student Tutoring', 'Course Guidance', 'Enrollment Help', 'Study Resources', 'Career Advice']
    },
    {
      icon: Store,
      title: '	Configurable Retention & Redaction: ',
      description: 'Lets organisations define how long interaction data is kept, and supports redaction of sensitive fields to align with privacy policies.',
      benefits: ['Product Discovery', 'Order Status', 'Returns Processing', 'Size Guidance', 'Shopping Assistance']
    },

  ];

  const benefits = [
    {
      icon: Zap,
      title: 'Reduced Call-Center & Ticket Volume',
      description: 'Takes care of high-frequency, low-complexity questions so human agents can focus on edge cases and high-value interactions.',
    },
    {
      icon: Cloud,
      title: '	24/7, Always-On Support',
      description: 'Ensures customers, patients, students or employees can get reliable help outside office hours and across time zones.',
    },
    {
      icon: Users,
      title: 'Consistent, On-Brand Answers',
      description: 'Delivers the same approved information every time, reducing errors and miscommunication across channels.',
    },
    {
      icon: TrendingUp,
      title: '	Faster Time-to-Resolution',
      description: 'Guides users directly to the right information or next step, shortening journeys and improving satisfaction scores.',
    },
    {
      icon: Brain,
      title: '	Scalable Customer & Employee Experience',
      description: 'As demand grows, the avatar can handle more concurrent conversations without compromising on responsiveness or quality.',
    }
  ];

  return (
    <div ref={containerRef} className="relative min-h-screen pt-20">
      {/* Animated Background */}
      <motion.div
        style={{ y: backgroundY }}
        className="fixed inset-0 opacity-20"
      >
        <div className="absolute inset-0">
          {Array.from({ length: 8 }).map((_, i) => (
            <motion.div
              key={i}
              className="absolute w-6 h-6 bg-amber-500 rounded-full"
              style={{
                left: `${Math.random() * 100}%`,
                top: `${Math.random() * 100}%`,
              }}
              animate={{
                scale: [0, 1, 0],
                opacity: [0, 1, 0],
              }}
              transition={{
                duration: 7,
                delay: i * 0.6,
                repeat: Infinity,
                repeatType: "loop"
              }}
            />
          ))}
        </div>
      </motion.div>

      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            {/* Content */}
            <motion.div
              initial={{ opacity: 0, x: -100 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
            >
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.2 }}
                className="flex items-center gap-3 mb-6"
              >
                <div className="p-3 rounded-xl bg-gradient-to-br from-yellow-500 to-amber-600 neon-glow">
                  <UserSquare2 className="w-8 h-8 text-white" />
                </div>
                <div>
                  <span className="text-sm font-medium text-yellow-400 uppercase tracking-wide">Cloud-Native Multilingual Avatar</span>
                  <h1 className="text-4xl lg:text-6xl font-bold">
                    <span className="bg-gradient-to-r from-yellow-500 to-amber-600 bg-clip-text text-transparent">WhispeRAGon</span>
                  </h1>
                  <p className="text-sm text-muted-foreground italic">Digital Human Platform</p>
                </div>
              </motion.div>

              <motion.p
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.4 }}
                className="text-xl text-muted-foreground mb-8 leading-relaxed text-justify"
              >
               WhispeRAGon is a digital human avatar that combines open-source large language models, Whisper speech recognition, Ollama text generation, lip-synced animation and retrieval-augmented memory into a single experience. Users can speak or type in their preferred language, and the avatar responds instantly with grounded, domain-specific answers. Because it runs in the cloud and plugs into your own content, policies and data sources, WhispeRAGon becomes a front-line assistant for customer portals, internal service desks and high-volume information channels.
              </motion.p>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.6 }}
                className="flex flex-col sm:flex-row gap-4 mb-12"
              >
                <Link
                  to="/contact"
                  className="group inline-flex items-center justify-center gap-3 bg-gradient-to-r from-yellow-500 to-amber-600 px-8 py-4 rounded-xl text-white font-semibold hover:shadow-2xl hover:shadow-yellow-500/25 transition-all duration-300"
                >
                  Request Platform Demo
                  <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                </Link>
                <button className="inline-flex items-center justify-center gap-3 glass px-8 py-4 rounded-xl text-white font-semibold hover:glass-strong transition-all duration-300">
                  <Play className="w-5 h-5" />
                  Watch Demo
                </button>
              </motion.div>

              {/* Key Metrics */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.8 }}
                className="grid grid-cols-3 gap-8 hidden"
              >
                <div className="text-center">
                  <div className="text-2xl font-bold text-yellow-500">Cloud</div>
                  <div className="text-sm text-muted-foreground">Native</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-amber-400">Multi</div>
                  <div className="text-sm text-muted-foreground">Lingual</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-accent">Human-Like</div>
                  <div className="text-sm text-muted-foreground">Interaction</div>
                </div>
              </motion.div>
            </motion.div>

            {/* Hero Image */}
            <motion.div
              initial={{ opacity: 0, x: 100, rotateY: -15 }}
              animate={{ opacity: 1, x: 0, rotateY: 0 }}
              transition={{ duration: 0.8, delay: 0.3 }}
              className="relative"
            >
              <div className="relative group">
                <motion.div
                  animate={{ rotate: 360 }}
                  transition={{ duration: 25, repeat: Infinity, ease: 'linear' }}
                  className="absolute -inset-4 bg-gradient-to-r from-yellow-500 via-amber-500 to-orange-500 rounded-2xl blur-3xl opacity-20"
                />
                <div className="relative overflow-hidden rounded-2xl border border-white/10 shadow-2xl">
                  <ImageWithFallback
                    src="/assets/Products/ai-Products/whispeRAGon.png"
                    alt="WhispeRAGon Digital Human Avatar Platform"
                    className="w-full h-auto"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-background/80 via-transparent to-transparent" />
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Core Features Section */}
      <section className="relative py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl lg:text-5xl mb-4">
              Core <span className="bg-gradient-to-r from-yellow-400 to-amber-400 bg-clip-text text-transparent">Features</span>
            </h2>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto text-center">
              Advanced AI technology for natural human-computer interaction
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => {
              const Icon = feature.icon;
              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  className="glass-strong rounded-xl p-6 hover:scale-105 transition-all duration-300 group"
                >
                  <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-yellow-500/20 to-amber-500/20 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300">
                    <Icon className="w-6 h-6 text-yellow-400" />
                  </div>
                  <h3 className="font-semibold text-lg mb-2 text-white">{feature.title}</h3>
                  <p className="text-sm text-muted-foreground text-justify">{feature.description}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* WhispeRAGon Interaction Flow - Hardcoded from Image */}
      <section className="relative py-24 bg-white/[0.02] hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl lg:text-5xl mb-4">
              WhispeRAGon <span className="bg-gradient-to-r from-yellow-400 to-amber-400 bg-clip-text text-transparent">Interaction Flow</span>
            </h2>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto mb-4 text-center">
              Interaction Sequence
            </p>
            <p className="text-base text-muted-foreground max-w-4xl mx-auto text-center">
              The interaction flow includes user voice/text input, Whisper/Ollama processing, vector DB retrieval, LLM reasoning, TTS & viseme, and lip-sync avatar reply. Memory layer personalizes follow-ups.
            </p>
          </motion.div>

          {/* Hardcoded Flowchart from Provided Image */}
          <div className="relative">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6 relative">
              {interactionFlow.map((step, index) => {
                const Icon = step.icon;
                return (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5, delay: index * 0.1 }}
                    className="glass-strong rounded-xl p-6 relative group hover:border-yellow-500/50 transition-all duration-300"
                  >
                    <div className="flex flex-col items-center text-center">
                      <div className={`w-16 h-16 rounded-full bg-gradient-to-br ${step.color} flex items-center justify-center mb-4 shadow-lg shadow-yellow-500/30 group-hover:scale-110 transition-transform duration-300`}>
                        <Icon className="w-8 h-8 text-white" />
                      </div>
                      <div className="text-xs font-bold text-yellow-400 mb-2">STEP {step.step}</div>
                      <h3 className="font-semibold text-lg mb-2 text-white">{step.title}</h3>
                      <p className="text-xs text-muted-foreground mb-3">{step.subtitle}</p>
                      <p className="text-xs text-muted-foreground/80 text-justify">{step.description}</p>
                    </div>
                    {/* Arrow - only show on larger screens and not on last item in row */}
                    {((index + 1) % 3 !== 0 || index === interactionFlow.length - 1) && index < interactionFlow.length - 1 && (
                      <div className="hidden lg:block absolute top-1/2 -right-3 transform -translate-y-1/2 z-10">
                        <ChevronRight className="w-6 h-6 text-yellow-400" />
                      </div>
                    )}
                  </motion.div>
                );
              })}
            </div>

            {/* Enterprise Integration Note */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.6 }}
              className="mt-12 glass rounded-xl p-6 border border-yellow-500/30"
            >
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-yellow-500/20 to-amber-500/20 flex items-center justify-center flex-shrink-0">
                  <Network className="w-6 h-6 text-yellow-400" />
                </div>
                <div>
                  <h3 className="font-semibold text-lg mb-2 text-white">Enterprise Integration</h3>
                  <p className="text-sm text-muted-foreground text-justify">
                    The web interface scales to meet enterprise multi-tenant demand, making WhispeRAGon a scalable solution for businesses looking to enhance user engagement. Built with cloud-native architecture, the platform supports high availability, load balancing, and seamless integration with existing enterprise systems through REST APIs and webhooks.
                  </p>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Platform Capabilities Section */}
      <section className="relative py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl lg:text-5xl mb-4">
               <span className="bg-gradient-to-r from-yellow-400 to-amber-400 bg-clip-text text-transparent">Solutions</span>
            </h2>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto text-center">
              WhispeRAGon fits wherever people ask repeat questions, need guidance through complex information, or expect instant answers regardless of time zone. A single deployment can support multiple business units and brands.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-8">
            {solutions.map((solution, index) => {
              const Icon = solution.icon;
              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  className="glass-strong rounded-xl p-8 hover:scale-105 transition-all duration-300"
                >
                  <div className="flex items-start gap-4 mb-6">
                    <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-yellow-500/20 to-amber-500/20 flex items-center justify-center flex-shrink-0">
                      <Icon className="w-7 h-7 text-yellow-400" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-xl mb-2 text-white">{solution.title}</h3>
                      <p className="text-sm text-muted-foreground text-justify">{solution.description}</p>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-3 hidden">
                    {solution.features.map((feature, idx) => (
                      <div key={idx} className="flex items-center gap-2 text-sm text-muted-foreground">
                        <CheckCircle className="w-4 h-4 text-yellow-400 flex-shrink-0" />
                        <span>{feature}</span>
                      </div>
                    ))}
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Industry Verticals Section */}
      <section className="relative py-24 bg-white/[0.02]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl lg:text-5xl mb-4">
              Data Security &  <span className="bg-gradient-to-r from-amber-400 to-orange-400 bg-clip-text text-transparent">Compliance</span>
            </h2>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto text-center">
              WhispeRAGon is designed with enterprise security expectations in mind. It respects organisational boundaries, keeps sensitive data protected and offers the controls teams need to meet internal and external compliance requirements.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {compliances.map((compliance, index) => {
              const Icon = compliance.icon;
              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  className="glass-strong rounded-xl p-8 hover:scale-105 transition-all duration-300"
                >
                  <div className="flex items-start gap-4 mb-6">
                    <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-amber-500/20 to-orange-500/20 flex items-center justify-center flex-shrink-0">
                      <Icon className="w-7 h-7 text-amber-400" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-xl mb-2 text-white">{compliance.title}</h3>
                      <p className="text-sm text-muted-foreground text-justify">{compliance.description}</p>
                    </div>
                  </div>
                  <ul className="space-y-2 hidden">
                    {compliance.benefits.map((benefit, idx) => (
                      <li key={idx} className="flex items-start gap-2 text-sm text-muted-foreground">
                        <CheckCircle className="w-4 h-4 text-amber-400 flex-shrink-0 mt-0.5" />
                        <span>{benefit}</span>
                      </li>
                    ))}
                  </ul>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="relative py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl lg:text-5xl mb-4">
              Platform <span className="bg-gradient-to-r from-yellow-400 to-amber-400 bg-clip-text text-transparent">Benefits</span>
            </h2>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto text-center">
             For growing organisations, WhispeRAGon is less about flashy avatars and more about practical business outcomes. It helps teams handle more conversations, in more languages, without constantly expanding headcount.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {benefits.map((benefit, index) => {
              const Icon = benefit.icon;
              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  className="glass rounded-xl p-6 border-l-4 border-yellow-500/50 hover:border-yellow-500 transition-all duration-300"
                >
                  <Icon className="w-8 h-8 text-yellow-400 mb-3" />
                  <h3 className="font-semibold text-lg mb-2 text-white">{benefit.title}</h3>
                  <p className="text-sm text-muted-foreground mb-3 text-justify">{benefit.description}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="relative py-24 bg-white/[0.02] hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl lg:text-5xl mb-4">
              Customer <span className="bg-gradient-to-r from-yellow-400 to-amber-400 bg-clip-text text-transparent">Testimonials</span>
            </h2>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto text-center">
              What our customers are saying about WhispeRAGon
            </p>
          </motion.div>

          <ProductTestimonials 
            testimonials={whispeRAGonTestimonials}
            title="Digital Human Success"
            subtitle="See how organizations transformed customer engagement with WhispeRAGon"
            gradientFrom="#FFC107"
            gradientVia="#FF9800"
            gradientTo="#FFC107"
            iconColor="#FFC107"
            dotColor="bg-amber-500"
            speed={60}
            direction="left"
          />
        </div>
      </section>

      {/* CTA Section */}
      <section className="relative py-24">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="glass-strong rounded-2xl p-12 text-center border border-yellow-500/30 relative overflow-hidden"
          >
            <div className="absolute inset-0 bg-gradient-to-br from-yellow-500/10 via-transparent to-amber-500/10" />
            <div className="relative z-10">
              <h2 className="text-3xl lg:text-4xl mb-4 text-white">
                Ready to Deploy Your <span className="bg-gradient-to-r from-yellow-400 to-amber-400 bg-clip-text text-transparent">Digital Human?</span>
              </h2>
              <p className="text-lg text-muted-foreground mb-8 max-w-2xl mx-auto text-justify">
                Schedule a demo to see how WhispeRAGon can enhance customer engagement, reduce support costs, and provide 24/7 multilingual assistance across your organization.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link
                  to="/contact"
                  className="inline-flex items-center justify-center gap-3 bg-gradient-to-r from-yellow-500 to-amber-600 text-white px-8 py-4 rounded-xl font-semibold shadow-lg shadow-yellow-500/25 hover:shadow-yellow-500/40 transition-all duration-300"
                >
                  Schedule Demo
                  <ArrowRight className="w-5 h-5" />
                </Link>
                <Link
                  to="/contact"
                  className="inline-flex items-center justify-center gap-3 glass px-8 py-4 rounded-xl font-semibold border border-yellow-500/30 hover:border-yellow-500/50 transition-all duration-300 text-white"
                >
                  Contact Sales
                </Link>
              </div>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default WhispeRAGon;